import { API_BASE_URL } from '../api';
import { resolveGarmentInspirationForProductType } from '../utils/garmentInspirationRef';
import { postGenerationJob } from './generationJob.api';

export const generateRealtimeContent = async (
  productImageUrl, 
  productCategory, 
  productType, 
  userImageUrl = null,
  saveToKit = false,
  kitOptions = {},
  useGPTModel = false,
  isStitched = false,
  colorVariants = []
) => {
  // Extract brandProfile from kitOptions to pass at top level
  const { brandProfile, ...restKitOptions } = kitOptions;
  
  const payload = {
    productImageUrl,
    productCategory,
    productType,
    ...(kitOptions.productTypeCustomizations && { productTypeCustomizations: kitOptions.productTypeCustomizations }),
    ...(kitOptions.productTypeCustomizationReferences && { productTypeCustomizationReferences: kitOptions.productTypeCustomizationReferences }),
    ...(kitOptions.garmentInspirationImage && { garmentInspirationImage: kitOptions.garmentInspirationImage }),
    saveToKit,
    useGPTModel,
    isStitched,
    colorVariants,
    ...(userImageUrl && { userImageUrl }),
    ...(kitOptions.inspirationalDesignImage && { inspirationalDesignImage: kitOptions.inspirationalDesignImage }),
    ...(kitOptions.sourceInspirationalDesignId && {
      sourceInspirationalDesignId: String(kitOptions.sourceInspirationalDesignId)
    }),
    ...(brandProfile && { brandProfile }), // Pass brandProfile at top level for backend
    ...(saveToKit && restKitOptions),
    // Ensure advancedSettings are at the top level for backend extraction
    ...(kitOptions.advancedSettings && { advancedSettings: kitOptions.advancedSettings }),
    ...(kitOptions.kidAge && { kidAge: kitOptions.kidAge })
  };
  console.log("Realtime API payload:", JSON.stringify(payload, null, 2))
  console.log("Realtime API useGPTModel value:", payload.useGPTModel)
  console.log("Realtime API useGPTModel type:", typeof payload.useGPTModel)
  return postGenerationJob(`${API_BASE_URL}/realtime/generate`, payload, {
    maxWaitMs: 8 * 60 * 1000,
  });
};


// Correct implementation: Multiple API calls for product types, single color variants array per call
export const generateMultipleKits = async (
  productImageUrl,
  productCategory,
  isStitched = false,
  productTypes,
  userImageUrl = null,
  kitOptions = {},
  useGPTModel = false,
  onProgress = null,
  colorVariants = []
) => {
  console.log(`🚀 Starting generation for ${productTypes.length} product types with ${colorVariants.length} color variants each...`);

  // Create an array to track progress
  const progressTracker = {
    completed: 0,
    total: productTypes.length,
    results: [],
    errors: []
  };

  // Helper function to update progress
  const updateProgress = (productType, status, data = null, error = null) => {
    // Only increment completed count when a kit is actually finished (completed or error)
    if (status === 'completed' || status === 'error') {
      progressTracker.completed++;
    }
    
    if (onProgress) {
      onProgress({
        current: progressTracker.completed,
        total: progressTracker.total,
        productType: productType.finalLabel,
        status,
        error
      });
    }
  };

  // Create all API calls as promises - ONE CALL PER PRODUCT TYPE
  const kitPromises = productTypes.map(async (productType, index) => {
    try {
      // Update progress to show generation started
      updateProgress(productType, 'generating');

      // Extract brandProfile and advancedSettings from kitOptions to pass at top level
      const {
        brandProfile,
        advancedSettings,
        sourceProductId: optSourceProductId,
        productId: optProductId,
        ...restKitOptions
      } = kitOptions || {};
      const linkedFabricProductId = optSourceProductId || optProductId;
      const garmentInspirationImage = resolveGarmentInspirationForProductType(
        productType,
        kitOptions.kidAge
      );

      const payload = {
        productImageUrl,
        productCategory: productType.category || productCategory,
        productType: productType.finalValue,
        productTypeCustomizations: productType.customization || null,
        productTypeCustomizationReferences: productType.customizationReferences || null,
        ...(garmentInspirationImage && { garmentInspirationImage }),
        saveToKit: true,
        useGPTModel,
        isStitched,
        colorVariants, // Pass ALL color variants in single array
        ...(userImageUrl && { userImageUrl }),
        ...(kitOptions.inspirationalDesignImage && { inspirationalDesignImage: kitOptions.inspirationalDesignImage }),
        ...(kitOptions.sourceInspirationalDesignId && {
          sourceInspirationalDesignId: String(kitOptions.sourceInspirationalDesignId)
        }),
        ...(brandProfile && { brandProfile }), // Pass brandProfile at top level
        ...(advancedSettings && { advancedSettings }), // Pass advancedSettings at top level
        ...(linkedFabricProductId && { sourceProductId: String(linkedFabricProductId) }),
        ...(kitOptions.kidAge && { kidAge: kitOptions.kidAge }),
        // Prefer real fabric identity from the wizard; fall back to style label only if missing.
        productName:
          (kitOptions.productName && String(kitOptions.productName).trim()) ||
          productType.finalLabel,
        productDescription:
          (kitOptions.productDescription && String(kitOptions.productDescription).trim()) ||
          `Generated ${productType.finalLabel} content`,
        kitOptions: {
          ...restKitOptions,
          kitName:
            kitOptions.kitName ||
            `${productType.finalLabel} Kit - ${new Date().toLocaleDateString()}`,
          productName:
            (kitOptions.productName && String(kitOptions.productName).trim()) ||
            productType.finalLabel,
          productDescription:
            (kitOptions.productDescription && String(kitOptions.productDescription).trim()) ||
            `Generated ${productType.finalLabel} content`,
        }
      };

      console.log(`🎨 Generating ${productType.finalLabel} with ${colorVariants.length} color variants (${index + 1}/${productTypes.length})`);
      console.log(`📦 Color variants for ${productType.finalLabel}:`, colorVariants.map(cv => cv.colorName));
      if (garmentInspirationImage) {
        console.log(`👗 Garment inspiration reference for ${productType.finalLabel}:`, garmentInspirationImage);
      }

      const data = await postGenerationJob(`${API_BASE_URL}/realtime/generate`, payload, {
        maxWaitMs: 8 * 60 * 1000,
      });

      const result = {
        productType: productType.finalLabel,
        productTypeValue: productType.finalValue,
        data,
        success: true
      };

      // Update progress for success
      updateProgress(productType, 'completed');
      
      return result;

    } catch (error) {
      console.error(`Error generating kit for ${productType.finalLabel}:`, error);
      
      const errorResult = {
        productType: productType.finalLabel,
        productTypeValue: productType.finalValue,
        error: error.response?.data?.message || error.message,
        success: false
      };

      // Update progress for error
      updateProgress(productType, 'error', null, error.response?.data?.message || error.message);
      
      return errorResult;
    }
  });

  // Execute all promises in parallel
  console.log(`🚀 Starting parallel generation of ${productTypes.length} product types...`);
  const startTime = Date.now();
  
  const allResults = await Promise.allSettled(kitPromises);
  
  const endTime = Date.now();
  console.log(`✅ Parallel generation completed in ${endTime - startTime}ms`);

  // Process results
  const results = [];
  const errors = [];

  allResults.forEach((result, index) => {
    if (result.status === 'fulfilled') {
      if (result.value.success) {
        results.push(result.value);
      } else {
        errors.push(result.value);
      }
    } else {
      // Handle promise rejection
      const productType = productTypes[index];
      const errorResult = {
        productType: productType.finalLabel,
        productTypeValue: productType.finalValue,
        error: result.reason?.message || 'Unknown error occurred',
        success: false
      };
      errors.push(errorResult);
    }
  });

  return {
    results,
    errors,
    totalGenerated: results.length,
    totalErrors: errors.length,
    success: errors.length === 0,
    processingTime: endTime - startTime,
    kits: results,
    message: `Successfully generated ${results.length} product types with ${colorVariants.length} color variants each!`
  };
};

export const generateVideoContent = async (
  imageUrl,
  productCategory,
  productType,
  durationSeconds = 5,
  kitId = null
) => {
  const payload = {
    imageUrl,
    productCategory,
    productType,
    durationSeconds,
    ...(kitId && { kitId })
  };

  return postGenerationJob(`${API_BASE_URL}/realtime/generate-video`, payload, {
    maxWaitMs: 8 * 60 * 1000,
  });
};

export const generateZoomedOutVideo = async (
  productCategory,
  productType,
  referenceImageUrl,
  kitId = null,
  useGPTModel = false
) => {
  const payload = {
    productCategory,
    productType,
    referenceImageUrl,
    useGPTModel,
    ...(kitId && { kitId })
  };

  return postGenerationJob(`${API_BASE_URL}/virtual-tryon/zoomed-out-video`, payload, {
    maxWaitMs: 8 * 60 * 1000,
  });
};

// POST /api/veo/generate — create a fashion video of a generated concept using Google Veo
export const generateVeoVideo = async ({
  prompt,
  imageUrl = null,
  aspectRatio = null,
  durationSeconds = null,
  negativePrompt = null,
  model = null,
  kitId = null
}) => {
  const payload = {
    prompt,
    ...(imageUrl && { imageUrl }),
    ...(aspectRatio && { aspectRatio }),
    ...(typeof durationSeconds === 'number' && { durationSeconds }),
    ...(negativePrompt && { negativePrompt }),
    ...(model && { model }),
    ...(kitId && { kitId })
  };

  return postGenerationJob(`${API_BASE_URL}/veo/generate`, payload, {
    maxWaitMs: 10 * 60 * 1000,
  });
}; 

export const applyFabricToGarment = async ({
  garmentUrl,
  fabricUrl,
  aspectRatio = '2:3',
  outputFormat = 'jpg',
  useNanoBananaPro = false,
  productCategory = 'women-clothing',
  productType = 'other',
  advancedSettings = null,
  saveToKit = true,
  sourceProductId = null,
  sourceInspirationalDesignId = null,
  kitName = null,
  productName = null,
  fabricName = null
}) => {
  return postGenerationJob(
    `${API_BASE_URL}/realtime/apply-fabric-to-garment`,
    {
      garmentUrl,
      fabricUrl,
      aspectRatio,
      outputFormat,
      useNanoBananaPro,
      productCategory,
      productType,
      saveToKit,
      ...(sourceProductId && { sourceProductId: String(sourceProductId) }),
      ...(sourceInspirationalDesignId && {
        sourceInspirationalDesignId: String(sourceInspirationalDesignId)
      }),
      ...(kitName && { kitName }),
      ...(productName && { productName }),
      ...(fabricName && { fabricName }),
      ...(advancedSettings && { advancedSettings })
    },
    { maxWaitMs: 8 * 60 * 1000 }
  );
};

export const upscaleUploadedImage = async ({
  image,
  upscale_factor = 'x4',
  compression_quality = 80
}) => {
  return postGenerationJob(
    `${API_BASE_URL}/realtime/upscale-image`,
    { image, upscale_factor, compression_quality },
    { maxWaitMs: 8 * 60 * 1000 }
  );
};
/**
 * Generate one lookbook image with four concepts from fabric recommendations:
 * Women, Men, Kids Boy, Kids Girl — same fabric on every garment.
 *
 * Provide either:
 * - concepts: [{ audience, type_value, type_label, reason?, category? }, ...] (4 items), or
 * - sourceProductId / fabricAnalysis so the server can pick rank-1 insights per audience
 */
export const generateMultiConceptImage = async ({
  productImageUrl,
  concepts = null,
  fabricAnalysis = null,
  sourceProductId = null,
  saveToKit = false,
  kitName = null,
  productName = null,
  productDescription = null,
  brandProfile = null,
  advancedSettings = null,
} = {}) => {
  const payload = {
    productImageUrl,
    saveToKit,
    ...(Array.isArray(concepts) && concepts.length > 0 && { concepts }),
    ...(fabricAnalysis && { fabricAnalysis }),
    ...(sourceProductId && { sourceProductId: String(sourceProductId) }),
    ...(kitName && { kitName }),
    ...(productName && { productName }),
    ...(productDescription && { productDescription }),
    ...(brandProfile && { brandProfile }),
    ...(advancedSettings && { advancedSettings }),
  };

  return postGenerationJob(
    `${API_BASE_URL}/realtime/generate-multi-concept`,
    payload,
    { maxWaitMs: 8 * 60 * 1000 }
  );
};
